import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { X, Send, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactFormModal = ({ isOpen, onClose, type = 'support' }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    type: type,
  });

  // Update form data when modal opens or user changes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: user?.email?.split('@')[0] || '',
        email: user?.email || '',
        subject: '',
        message: '',
        type: type,
      });
    }
  }, [isOpen, user, type]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    const emailToUse = formData.email || user?.email;
    
    if (!emailToUse || !formData.message) {
      toast.error('Please fill in email and message');
      return;
    }

    if (!emailToUse.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name || user?.email?.split('@')[0] || 'Anonymous',
        email: emailToUse,
        subject: formData.subject || 'No subject',
        message: formData.message,
        type: formData.type,
        user_id: user?.id || null,
        user_email: user?.email || emailToUse,
        timestamp: new Date().toISOString(),
      };

      console.log('Submitting contact form:', payload);

      await axios.post(`${API}/support/contact`, payload);
      
      setSubmitted(true);
      toast.success('Message sent successfully! We\'ll respond within 24-48 hours.');
      
      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
        setSubmitted(false);
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          type: 'support',
        });
      }, 2000);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'feedback':
        return '💬 Send Feedback';
      case 'privacy':
        return '🛡️ Privacy Request';
      default:
        return '📧 Contact Support';
    }
  };

  const getDescription = () => {
    switch (type) {
      case 'feedback':
        return 'Share your thoughts, suggestions, or report issues';
      case 'privacy':
        return 'Submit privacy-related questions or data requests (GDPR/CCPA)';
      default:
        return 'Get help with any questions or issues';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="relative">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="absolute top-4 right-4"
          >
            <X className="w-4 h-4" />
          </Button>
          
          {submitted ? (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <CardTitle className="text-2xl mb-2">Message Sent!</CardTitle>
              <CardDescription>
                We've received your message and will respond to your email within 24-48 hours.
              </CardDescription>
            </div>
          ) : (
            <>
              <CardTitle className="text-2xl">{getTitle()}</CardTitle>
              <CardDescription>{getDescription()}</CardDescription>
            </>
          )}
        </CardHeader>

        {!submitted && (
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name */}
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  disabled={loading}
                />
              </div>

              {/* Email */}
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  disabled={loading || !!user?.email}
                  required
                />
                {user?.email && (
                  <p className="text-xs text-gray-500 mt-1">
                    Using your account email
                  </p>
                )}
              </div>

              {/* Subject */}
              <div>
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  placeholder={
                    type === 'privacy' 
                      ? 'e.g., GDPR Data Request' 
                      : type === 'feedback'
                      ? 'e.g., Feature suggestion'
                      : 'e.g., Issue with video analysis'
                  }
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  disabled={loading}
                />
              </div>

              {/* Message */}
              <div>
                <Label htmlFor="message">Message *</Label>
                <Textarea
                  id="message"
                  placeholder={
                    type === 'privacy'
                      ? 'Please describe your privacy request or question...'
                      : type === 'feedback'
                      ? 'Share your feedback, suggestions, or report an issue...'
                      : 'Describe your question or issue in detail...'
                  }
                  rows={6}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  disabled={loading}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  {type === 'privacy' && 'For GDPR/CCPA requests, please include "GDPR" or "CCPA" in your message'}
                </p>
              </div>

              {/* Submit Button */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={loading}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </div>

              {/* Info */}
              <div className="bg-blue-50 p-4 rounded-lg text-sm text-gray-700">
                <p className="font-semibold mb-1">📬 What happens next?</p>
                <ul className="space-y-1 text-xs">
                  <li>• Your message will be sent to our admin team</li>
                  <li>• We'll review and respond within 24-48 hours</li>
                  <li>• You'll receive a reply at the email address provided</li>
                  <li>• For urgent issues, please mark "URGENT" in the subject</li>
                </ul>
              </div>
            </form>
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default ContactFormModal;
